import { useEffect } from 'react';

// One guard that gives every dialog in the app the same close discipline.
//
// MUI closes a Dialog when the user presses Escape or clicks the backdrop.
// On a long form that is far too easy to do by accident, and the work is gone.
// Instead of editing ~300 dialog call sites, this guard intercepts both
// gestures at the document level, in the *capture* phase, so it runs before
// MUI's own handlers and before React's root listeners:
//
//   Escape            -> swallowed for every dialog
//   backdrop click    -> swallowed for every dialog
//
// A dialog that genuinely wants one of those gestures opts back in with a data
// attribute (unknown props are forwarded to the modal root element):
//
//   <Dialog data-allow-escape-close="true">      // Escape works again
//   <Dialog data-allow-backdrop-close="true">    // backdrop click works again
//
// Explicit close paths are untouched: the Cancel/Close/Save buttons, an
// `onClose` your own code calls, and programmatic navigation all keep working.
const DIALOG_ROOT_SELECTOR = '.MuiDialog-root';
const DIALOG_PAPER_SELECTOR = '.MuiDialog-paper';

const openDialogRoots = () => Array.from(document.querySelectorAll(DIALOG_ROOT_SELECTOR));

const attrTrue = (element, name) => Boolean(
  element
  && typeof element.getAttribute === 'function'
  && element.getAttribute(name) === 'true'
);

const allowsEscapeClose = (root) => attrTrue(root, 'data-allow-escape-close') || attrTrue(root, 'data-allow-backdrop-close');

const allowsBackdropClose = (root) => attrTrue(root, 'data-allow-backdrop-close');

export default function DialogCloseGuard() {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const swallow = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    };

    // A click lands "outside" the dialog when it is inside a dialog root but not
    // inside the paper. That covers the backdrop and the container padding.
    const handlePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const root = target.closest(DIALOG_ROOT_SELECTOR);
      if (!root) return;
      if (target.closest(DIALOG_PAPER_SELECTOR)) return;
      if (allowsBackdropClose(root)) return;
      swallow(event);
    };

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      const roots = openDialogRoots();
      if (!roots.length) return;
      // Only the topmost dialog owns the gesture.
      if (allowsEscapeClose(roots[roots.length - 1])) return;
      swallow(event);
    };

    // mousedown is what moves focus, so guard it as well as click.
    document.addEventListener('mousedown', handlePointerDown, true);
    document.addEventListener('click', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true);
      document.removeEventListener('click', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  return null;
}
